// App.js
import React, { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import AdminPage from './AdminPage';
import DashboardPage from './DashboardPage';
import LandingPage from './LandingPage';
import LoginScreen from './LoginScreen';
import SignUpScreen from './SignUpScreen';
import ConfirmSignUpScreen from './ConfirmSignUpScreen';
import AppHeader from './AppHeader';
import SalesrepDashboard from './SalesRepDashboard';
import RetailerDashboard from './RetailerDashboard';
import OrderPage from './OrderPage';

import { Amplify } from 'aws-amplify';
import { fetchAuthSession } from '@aws-amplify/auth';
import { 
    signOut, 
    getCurrentUser, 
    fetchUserAttributes 
} from '@aws-amplify/auth/cognito';

import styles from './ChatApp.module.css';

// Import refactored utilities and components
import { useMediaQuery } from './utils/AppHelpers';
import { enrichCartItems, calculateCart, updateProductDefinitionMap } from './utils/CartLogic';
import ChatView from './components/Chat/ChatView';
import { CartModal } from './components/Cart/CartButtons';
import ImageModal from './components/ImageModal'; // Import the new dedicated ImageModal

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const TAX_RATE = 0.00; 

Amplify.configure({
    Auth: {
        Cognito: { 
            userPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID,
            userPoolClientId: process.env.REACT_APP_COGNITO_USER_POOL_CLIENT_ID, 
            region: process.env.REACT_APP_COGNITO_REGION
        }
    }
});

axios.interceptors.request.use(async (config) => {
    if (config.url === `${API_BASE_URL}/signup`) {
        return config;
    }
    try {
        const session = await fetchAuthSession();
        const token = session.tokens?.idToken?.toString();
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
    } catch (error) {
        // Not logged in
    }
    return config;
});


const ChatApp = () => {
    const [appView, setAppView] = useState('landing');
    const [confirmEmail, setConfirmEmail] = useState('');
    const [currentView, setCurrentView] = useState('chat');
    const [messages, setMessages] = useState([]);
    const [chatHistory, setChatHistory] = useState([]);
    const [cart, setCart] = useState({ items: [], subtotal: 0, tax: 0, total: 0 });
    const [isLoading, setIsLoading] = useState(false);
    const [enlargedImageUrl, setEnlargedImageUrl] = useState(null);
    const [isCartModalOpen, setIsCartModalOpen] = useState(false);
    const [currentUser, setCurrentUser] = useState(null);
    const [allStores, setAllStores] = useState([]);
    
    const [selectedStore, setSelectedStore] = useState(() => {
        try {
            const saved = localStorage.getItem('selectedStore');
            return saved ? JSON.parse(saved) : null;
        } catch (e) { return null; }
    });

    useEffect(() => {
        if (selectedStore) {
            localStorage.setItem('selectedStore', JSON.stringify(selectedStore));
        } else {
            localStorage.removeItem('selectedStore');
        }
    }, [selectedStore]);

    const [agentDisplayName, setAgentDisplayName] = useState('AirGrocer AI');
    const [isAuthenticating, setIsAuthenticating] = useState(true);
    const [isRecording, setIsRecording] = useState(false);
    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);

    const [productDefinitionMap, setProductDefinitionMap] = useState(new Map());

    const isMobile = useMediaQuery(768);

    // --- UTILITY HANDLERS ---
    
    // Wrap utility functions to avoid re-creating inline functions in effects/handlers
    const handleUpdateProductDefinitionMap = useCallback((products) => {
        updateProductDefinitionMap(products, setProductDefinitionMap);
    }, []);

    const handleEnrichCartItems = useCallback((cartItems, newProducts = []) => {
        return enrichCartItems(cartItems, newProducts, productDefinitionMap);
    }, [productDefinitionMap]);

    const handleCalculateCart = useCallback((items) => {
        return calculateCart(items);
    }, []);


    // --- AUTH AND INITIALIZATION ---

    useEffect(() => {
        const checkAuth = async () => {
            try {
                const cognitoUser = await getCurrentUser(); 
                await fetchAndSetUser(cognitoUser.username, true);
            } catch (error) {
                setAppView('landing');
            }
            setIsAuthenticating(false);
        };
        checkAuth();
    }, []); 

    const fetchAndSetUser = async (username, fetchStoresOnLogin = true) => {
        // ... (Logic to fetch user details and groups) ...
        try {
            const attributes = await fetchUserAttributes();
            const session = await fetchAuthSession();
            
            const idTokenPayload = session.tokens?.idToken?.payload;
            const groups = idTokenPayload ? idTokenPayload['cognito:groups'] : [];

            const user = {
                id: attributes.sub,
                name: attributes.name,
                email: attributes.email,
                type: groups && groups.length > 0 ? groups[0] : 'store'
            };
            
            setCurrentUser(user);
            setAppView('app');
            
            if (user.type === 'distributor' || user.type === 'sales_rep') {
                setCurrentView('dashboard');
            } else {
                setCurrentView('chat');
            }

            setMessages([
                { role: 'AirGrocer AI', content: `Welcome, ${user.name}! How can I help you today?` }
            ]);
            setChatHistory([]); 
            setAgentDisplayName('AirGrocer AI');
            
            if (fetchStoresOnLogin && (user.type === 'sales_rep' || user.type === 'distributor')) {
                let storeUrl = `${API_BASE_URL}/stores`;
                if (user.type === 'sales_rep') {
                    storeUrl += `?rep_id=${user.id}`;
                }
                try {
                    const response = await axios.get(storeUrl);
                    setAllStores(response.data);
                    let storeMessage = `I've loaded your assigned stores. Please select one from the header.`;
                    
                    if (selectedStore) {
                         const exists = response.data.find(s => s._id === selectedStore._id);
                         if (!exists) setSelectedStore(null);
                    }
                    
                    setMessages(prev => {
                        const lastMsg = prev[prev.length - 1];
                        if (lastMsg && lastMsg.content === storeMessage) {
                            return prev;
                        }
                        return [...prev, { role: 'AirGrocer AI', content: storeMessage }];
                    });
                    
                } catch (err) {
                    console.error("Failed to fetch stores", err);
                    setMessages(prev => [...prev, { role: 'AirGrocer AI', content: "I had trouble loading the store list." }]);
                }
            }
        } catch (err) {
            console.error("Error fetching user session/attributes:", err);
            setAppView('login');
        }
    };

    const handleLogin = (user) => {
        fetchAndSetUser(user.username, true);
    };

    const handleLogout = async () => {
        await signOut();
        setCurrentUser(null);
        setAllStores([]);
        setSelectedStore(null);
        localStorage.removeItem('selectedStore'); 
        setAppView('landing'); 
        setMessages([]);
        setChatHistory([]);
        setCurrentView('chat');
        setAgentDisplayName('AirGrocer AI');
        setProductDefinitionMap(new Map()); 
    };
    
    // --- CART FETCHING & SYNC ---

    const fetchCart = useCallback(async () => {
        if (!currentUser) return;
        
        let cartUrl = `${API_BASE_URL}/cart`;
        if (selectedStore && selectedStore._id) {
            cartUrl += `?store_id=${selectedStore._id}`;
        }
        
        try {
            const res = await axios.get(cartUrl);
            
            const cartItems = res.data.items || [];
            const initiallyEnrichedItems = handleEnrichCartItems(cartItems, []);
            setCart(handleCalculateCart(initiallyEnrichedItems)); 
        } catch (err) {
            console.error("Error fetching cart:", err);
        }
    }, [currentUser, selectedStore, handleEnrichCartItems, handleCalculateCart]); 

    const refreshCartImmediately = useCallback(async () => {
        let cartUrl = `${API_BASE_URL}/cart`;
        if (selectedStore && selectedStore._id) {
            cartUrl += `?store_id=${selectedStore._id}`;
        }
        try {
            const res = await axios.get(cartUrl);
            const cartItems = res.data.items || [];
            const initiallyEnrichedItems = handleEnrichCartItems(cartItems, []);
            setCart(handleCalculateCart(initiallyEnrichedItems)); 
        } catch (err) { console.error(err); }
    }, [selectedStore, handleEnrichCartItems, handleCalculateCart]);

    useEffect(() => {
        // Initial Fetch and Polling
        if (currentUser && !isLoading) {
            fetchCart();
        }
        const interval = setInterval(fetchCart, 2000);
        return () => clearInterval(interval);
    }, [currentUser, selectedStore, fetchCart, isLoading]); 


    // --- CHAT & ACTION HANDLERS ---

    const handleUpdateQuantity = (sku, quantity) => {
        if (quantity <= 0) {
            handleRemoveItem(sku);
            return;
        }
        
        const payload = { sku, quantity };
        if (selectedStore && selectedStore._id) payload.store_id = selectedStore._id;
        
        axios.post(`${API_BASE_URL}/update_item`, payload)
            .then((res) => {
                 if (res.data && res.data.items) {
                     const enriched = handleEnrichCartItems(res.data.items, []);
                     setCart(handleCalculateCart(enriched));
                 } else {
                     refreshCartImmediately();
                 }
            }) 
            .catch(err => {
                console.error("Failed to update item:", err);
                alert("Failed to update item. Please check your connection.");
                refreshCartImmediately(); 
            })
    };

    const handleRemoveItem = (sku) => {
        const payload = { sku };
        if (selectedStore && selectedStore._id) payload.store_id = selectedStore._id;

        axios.post(`${API_BASE_URL}/remove_item`, payload)
            .then(() => refreshCartImmediately()) 
            .catch(err => {
                console.error("Failed to remove item:", err);
                alert("Failed to remove item. Please check your connection.");
                refreshCartImmediately();
            })
    };

    const handleImageClick = (imageUrl) => {
        if (isMobile && imageUrl === true) {
             setIsCartModalOpen(true);
        } else {
            setEnlargedImageUrl(imageUrl);
        }
    };
    const handleCloseModal = () => setEnlargedImageUrl(null);

    const handleTextSubmit = async (inputValue, currentAgentName) => {
        const newUserMessage = { role: 'user', content: inputValue };
        setMessages(prev => [...prev, newUserMessage]);

        if (
            (currentUser.type === 'sales_rep' || currentUser.type === 'distributor') && 
            !selectedStore
        ) {
            const isOrderAttempt = inputValue.match(/(\d+)\s/g) || 
                                   inputValue.toLowerCase().includes('add') || 
                                   inputValue.toLowerCase().includes('checkout') ||
                                   inputValue.toLowerCase().includes('cart');
                                   
            if (isOrderAttempt) {
                setMessages(prev => [...prev, { role: agentDisplayName, content: "Please select a store from the header before placing or modifying an order." }]);
                return;
            }
        }

        setIsLoading(true);

        const loadingMessage = { role: currentAgentName, content: 'Thinking...' };
        setMessages(prev => [...prev, loadingMessage]);
    
        try {
            let user_context = { 
                selected_store: selectedStore || null,
                distributor_id: selectedStore ? selectedStore.distributor_id : null
            };
            const payload = { 
                user_input: inputValue, 
                chat_history: chatHistory,
                user_context: user_context 
            };

            const response = await axios.post(`${API_BASE_URL}/chat`, payload);
            const { agent_response, found_products, cart_data, new_chat_history, agent_name } = response.data;
            
            setAgentDisplayName(agent_name);
            setMessages(prev => prev.slice(0, -1)); 

            if (found_products && found_products.length > 0) {
                handleUpdateProductDefinitionMap(found_products);
            }

            const newMessagesToAdd = [];
            if (found_products && found_products.length > 0) {
                newMessagesToAdd.push({ role: agent_name, content: agent_response });
                newMessagesToAdd.push({ role: agent_name, type: 'product_selection', products: found_products, id: Date.now() });
            } else {
                newMessagesToAdd.push({ role: agent_name, content: agent_response });
            }
            setMessages(prev => [...prev, ...newMessagesToAdd]);
            setChatHistory(new_chat_history);

            // --- SERVER-AUTHORITATIVE UPDATE ---
            if (cart_data && cart_data.items) {
                const enrichedItems = handleEnrichCartItems(cart_data.items, found_products || []);
                setCart(handleCalculateCart(enrichedItems));
            } else {
                refreshCartImmediately(); 
            }

        } catch (error) {
            console.error("API Error:", error);
            setMessages(prev => [...prev.slice(0, -1), { role: agentDisplayName, content: 'Sorry, I ran into an error.' }]);
            refreshCartImmediately();
        } finally {
            setIsLoading(false);
        }
    };

    const handleImageUpload = async (file) => {
        if (!file) return;
        setIsLoading(true);
        const userMessage = { role: 'user', content: `Uploaded image: ${file.name}` };
        const loadingMessage = { role: agentDisplayName, content: 'Analyzing your image... 🖼️' };
        setMessages(prev => [...prev, userMessage, loadingMessage]);
        
        const formData = new FormData();
        formData.append('file', file);
        try {
            const response = await axios.post(`${API_BASE_URL}/identify_from_image`, formData);
            const { found_products, identified_text } = response.data;

            if (found_products && found_products.length > 0) {
                handleUpdateProductDefinitionMap(found_products);
            }

            setMessages(prev => prev.slice(0, -1)); 
            if (found_products && found_products.length > 0) {
                setMessages(prev => [
                    ...prev,
                    { role: agentDisplayName, content: `I found ${found_products.length} potential matches for items in your image.` },
                    { role: agentDisplayName, type: 'product_selection', products: found_products, id: Date.now() }
                ]);
            } else {
                const notFoundMsg = `I saw these items: "${identified_text}", but I couldn't find them in our catalog.`;
                setMessages(prev => [...prev, { role: agentDisplayName, content: notFoundMsg }]);
            }
        } catch (error) {
            console.error("Image Upload Error:", error);
            const errorMessage = "Sorry, I had trouble processing that image.";
            setMessages(prev => [...prev.slice(0, -1), { role: agentDisplayName, content: errorMessage }]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleAddItemsToCart = async (selectedItems, products) => {
        const itemsPayload = Object.entries(selectedItems)
            .filter(([, quantity]) => quantity > 0)
            .map(([sku, quantity]) => ({ sku, quantity: parseInt(quantity, 10) }));

        if (itemsPayload.length === 0) {
            setMessages(prev => [...prev, { role: agentDisplayName, content: "You didn't select a quantity for any item." }]);
            return;
        }
        
        const draftString = itemsPayload
            .map(item => `add ${item.quantity} ${item.sku}`)
            .join('\n');
        
        handleTextSubmit(draftString, agentDisplayName);
    };

    const handleQuickAdd = async (product) => {
        if (
            (currentUser.type === 'sales_rep' || currentUser.type === 'distributor') && 
            !selectedStore
        ) {
            alert("Please select a store from the header before adding items.");
            return; 
        }

        const draftString = `add 1 ${product.sku}`;
        handleTextSubmit(draftString, agentDisplayName);
    };
    
    const handleCheckout = () => {
        handleTextSubmit("checkout", agentDisplayName);
        if (isMobile) setIsCartModalOpen(false);
    };

    const handleAudioUpload = () => {
        if (isRecording) {
            mediaRecorderRef.current.stop();
            setIsRecording(false);
        } else {
            navigator.mediaDevices.getUserMedia({ audio: true })
                .then(stream => {
                    const mediaRecorder = new MediaRecorder(stream);
                    mediaRecorderRef.current = mediaRecorder;
                    audioChunksRef.current = [];
                    mediaRecorder.ondataavailable = event => {
                        audioChunksRef.current.push(event.data);
                    };
                    mediaRecorder.onstop = async () => {
                        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
                        const audioFile = new File([audioBlob], "voice_memo.webm", { type: 'audio/webm' });
                        stream.getTracks().forEach(track => track.stop());

                        setIsLoading(true);
                        const userMessage = { role: 'user', content: `[Voice Memo Sent]` };
                        const loadingMessage = { role: agentDisplayName, content: 'Transcribing audio... 🎤' };
                        setMessages(prev => [...prev, userMessage, loadingMessage]);
                        
                        const formData = new FormData();
                        formData.append('file', audioFile);
                        
                        try {
                            const response = await axios.post(`${API_BASE_URL}/chat/transcribe`, formData);
                            const { text } = response.data;
                            setMessages(prev => prev.slice(0, -1)); 
                            handleTextSubmit(text, agentDisplayName);
                        } catch (error) {
                            console.error("Transcription Error:", error);
                            setMessages(prev => [...prev.slice(0, -1), { role: agentDisplayName, content: "Sorry, I couldn't understand that." }]);
                        } finally {
                            setIsLoading(false);
                        }
                    };
                    mediaRecorder.start();
                    setIsRecording(true);
                })
                .catch(err => {
                    console.error("Audio recording error:", err);
                    setMessages(prev => [...prev, { role: agentDisplayName, content: "I need permission to access your microphone." }]);
                });
        }
    };

    const handleTextToSpeech = async (text) => {
        try {
            const response = await axios.post(`${API_BASE_URL}/chat/speak`, 
                { text },
                { responseType: 'blob' } 
            );
            const audioUrl = URL.createObjectURL(response.data);
            const audio = new Audio(audioUrl);
            audio.play();
        } catch (error) {
            console.error("TTS Error:", error);
        }
    };

    if (isAuthenticating) {
        return <div className={styles.loginContainer}>Loading...</div>;
    }
    
    if (appView === 'landing') {
        return <LandingPage 
            onNavigateToLogin={() => setAppView('login')} 
            onNavigateToSignUp={() => setAppView('signup')} 
        />;
    }
    if (appView === 'login') {
        return <LoginScreen 
            onLogin={handleLogin} 
            onNavigateToSignUp={() => setAppView('signup')} 
            onNavigateToLanding={() => setAppView('landing')}
            onNavigateToConfirm={(email) => {
                setConfirmEmail(email || ''); 
                setAppView('confirmSignup');
            }}
        />;
    }
    if (appView === 'signup') {
        return <SignUpScreen 
            onNavigateToConfirm={(email) => {
                setConfirmEmail(email); 
                setAppView('confirmSignup'); 
            }} 
            onNavigateToLogin={() => setAppView('login')}
            onNavigateToLanding={() => setAppView('landing')}
        />;
    }
    
    if (appView === 'confirmSignup') {
        return <ConfirmSignUpScreen
            defaultEmail={confirmEmail}
            onConfirmSuccess={() => setAppView('login')}
            onNavigateToLogin={() => setAppView('login')}
        />;
    }

    return (
        <div className={styles.appPage}>
            <div className={styles.appShell}>
            
                {currentUser && (
                    <AppHeader
                        currentUser={currentUser}
                        allStores={allStores}
                        selectedStore={selectedStore}
                        onStoreSelect={setSelectedStore}
                        onLogout={handleLogout}
                        currentView={currentView}
                        onNavigate={setCurrentView}
                    />
                )}
                
                {currentView === 'chat' && currentUser && (
                    <ChatView
                        messages={messages}
                        onTextSubmit={handleTextSubmit}
                        onImageUpload={handleImageUpload}
                        onAddItemsToCart={handleAddItemsToCart}
                        onQuickAdd={handleQuickAdd}
                        onImageClick={handleImageClick}
                        cart={cart}
                        onUpdateQuantity={handleUpdateQuantity}
                        onRemoveItem={handleRemoveItem}
                        isMobile={isMobile}
                        isLoading={isLoading}
                        onAudioUpload={handleAudioUpload}
                        onTextToSpeech={handleTextToSpeech}
                        isRecording={isRecording}
                        onParseDraft={(draftText) => handleTextSubmit(draftText, agentDisplayName)}
                        onCheckout={handleCheckout}
                    />
                )}
                
                {currentView === 'dashboard' && currentUser?.type === 'distributor' && <DashboardPage />}
                {currentView === 'dashboard' && currentUser?.type ==='sales_rep' && <SalesrepDashboard currentUser={currentUser} />}
                {currentView === 'dashboard' && currentUser?.type === 'store' && <RetailerDashboard currentUser={currentUser} />}

                {currentView === 'admin' && currentUser?.type === 'distributor' && <AdminPage />}
                
                {currentView === 'orders' && currentUser?.type === 'distributor' && <OrderPage />}

                <ImageModal imageUrl={enlargedImageUrl} onClose={handleCloseModal} />

                <CartModal
                    cart={cart}
                    isOpen={isCartModalOpen}
                    onClose={() => setIsCartModalOpen(false)}
                    onUpdateQuantity={handleUpdateQuantity}
                    onRemoveItem={handleRemoveItem}
                    onImageClick={handleImageClick}
                    onCheckout={handleCheckout}
                />
            </div>
        </div>
    );
};

export default ChatApp;